import * as XLSX from 'xlsx';

/** Excel 行（导出/导入共用列；nameEn/nameZh 仅导出参考） */
export type AliasExcelRow = {
    code: string;
    aliasEn: string;
    aliasZh?: string;
    normalSide?: string;
    currency?: string;
    nameEn?: string;
    nameZh?: string;
};

const HEADERS = [
    'code',
    'aliasEn',
    'aliasZh',
    'normalSide',
    'currency',
    'nameEn',
    'nameZh',
] as const;

function cellStr(v: unknown): string {
    if (v == null) return '';
    return String(v).trim();
}

function sanitizeFileName(name: string): string {
    return name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_').slice(0, 120) || 'company';
}

export function buildAliasExportFileName(companyName?: string, companyId?: number): string {
    const base = companyName?.trim()
        ? sanitizeFileName(companyName)
        : `company-${companyId ?? 'unknown'}`;
    return `${base}-co-gl-alias.xlsx`;
}

/**
 * 导出当前公司全量别名映射为 .xlsx（含标准科目 nameEn/nameZh 便于阅读）。
 */
export function exportAliasMappingsToXlsx(
    rows: Array<{
        code?: string;
        aliasEn?: string;
        aliasZh?: string;
        normalSide?: string;
        currency?: string;
    }>,
    leafNameMap: Map<string, { nameEn?: string; nameZh?: string }>,
    fileName: string,
): void {
    const data = rows.map((r) => {
        const code = (r.code || '').trim();
        const names = leafNameMap.get(code);
        return {
            code,
            aliasEn: (r.aliasEn || '').trim(),
            aliasZh: (r.aliasZh || '').trim(),
            normalSide: (r.normalSide || '').trim(),
            currency: (r.currency || 'HKD').trim(),
            nameEn: names?.nameEn || '',
            nameZh: names?.nameZh || '',
        };
    });

    const sheet = XLSX.utils.json_to_sheet(data, {header: [...HEADERS]});
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'AliasMappings');
    XLSX.writeFile(book, fileName);
}

/**
 * 解析导入文件。同 code 后者覆盖。忽略 nameEn/nameZh。
 * 缺 code / aliasEn 的行丢弃（由调用方计入 skip）。
 */
export async function parseAliasMappingsFromXlsx(file: File): Promise<{
    rows: AliasExcelRow[];
    skippedEmpty: number;
}> {
    const buf = await file.arrayBuffer();
    const book = XLSX.read(buf, {type: 'array'});
    const sheetName = book.SheetNames[0];
    if (!sheetName) return {rows: [], skippedEmpty: 0};

    const sheet = book.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {defval: ''});

    const byCode = new Map<string, AliasExcelRow>();
    let skippedEmpty = 0;

    for (const line of raw) {
        // 兼容大小写/空格表头
        const get = (...keys: string[]) => {
            for (const k of keys) {
                if (line[k] != null && cellStr(line[k]) !== '') return cellStr(line[k]);
                const found = Object.keys(line).find((h) => h.trim().toLowerCase() === k.toLowerCase());
                if (found) return cellStr(line[found]);
            }
            return '';
        };

        const code = get('code', 'GL Code', 'glCode');
        const aliasEn = get('aliasEn', 'Alias EN', 'alias_en');
        if (!code || !aliasEn) {
            skippedEmpty += 1;
            continue;
        }

        byCode.set(code, {
            code,
            aliasEn,
            aliasZh: get('aliasZh', 'Alias ZH', 'alias_zh') || undefined,
            normalSide: get('normalSide', 'Side', 'normal_side') || undefined,
            currency: get('currency', 'CCY') || undefined,
        });
    }

    return {rows: [...byCode.values()], skippedEmpty};
}
